/**
 * @architecture-header 1
 * @layer module
 * @domain curriculum
 * @responsibility Render one Curriculum unit surface with management controls and honest states.
 * @state-owner query state and local form state passed from the module boundary
 * @allowed-imports React; module model types; canonical Vispo loading state
 * @forbidden routes; app bootstrap; direct API clients; peer module internals
 * @public-api CurriculumDesktopSurface and CurriculumMobileSurface
 * @side-effects invokes caller-provided retry, selection, create, and update callbacks
 * @tests CurriculumUnitSurface.test.tsx
 */

import { useState } from 'react';
import { VispoLoading } from '../../../shared/components/WorkspaceState';
import type { CurriculumUnit } from '../model/types';

export type CurriculumSurfaceStatus = 'loading' | 'empty' | 'error' | 'denied' | 'ready';

export interface CurriculumUnitPatch {
  readonly name?: string;
  readonly knowledgeComponentIds?: readonly string[];
  readonly archived?: boolean;
}

interface SurfaceProps {
  readonly status: CurriculumSurfaceStatus;
  readonly units?: readonly CurriculumUnit[];
  readonly onRetry?: () => void;
  readonly onSelect?: (unit: CurriculumUnit) => void;
  readonly archivedUnits?: readonly CurriculumUnit[];
  readonly showingArchived?: boolean;
  readonly onShowArchived?: (show: boolean) => void;
  readonly onCreate?: (name: string, knowledgeComponentIds: readonly string[]) => void;
  readonly onUpdate?: (unit: CurriculumUnit, patch: CurriculumUnitPatch) => void;
  readonly mutationPending?: boolean;
  readonly mutationError?: unknown;
}

function State({ status, onRetry }: Pick<SurfaceProps, 'status' | 'onRetry'>) {
  if (status === 'loading') return <VispoLoading label="正在載入課綱…" />;
  if (status === 'empty') return <p role="status">這個班級還沒有課綱單元。</p>;
  if (status === 'denied') return <p role="alert">你目前沒有權限查看這個班級的課綱。</p>;
  if (status === 'error')
    return (
      <div role="alert">
        <p>課綱暫時無法載入。</p>
        {onRetry ? (
          <button type="button" onClick={onRetry}>
            重試
          </button>
        ) : null}
      </div>
    );
  return null;
}

function UnitList({
  units,
  onSelect,
  onUpdate,
  mutationPending,
}: Pick<SurfaceProps, 'units' | 'onSelect' | 'onUpdate' | 'mutationPending'>) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [kcText, setKcText] = useState('');

  return (
    <ol aria-label="課綱單元">
      {(units ?? []).map((unit) => (
        <li key={unit.id}>
          <button type="button" onClick={() => onSelect?.(unit)}>
            <strong>{unit.name}</strong>
            <span>{unit.knowledge_components.length} 個知識點</span>
            {unit.archived ? <em>已封存</em> : null}
          </button>
          {onUpdate ? (
            <>
              <button
                type="button"
                onClick={() => {
                  setEditingId(unit.id);
                  setName(unit.name);
                  setKcText(unit.knowledge_components.map((kc) => kc.id).join(', '));
                }}
              >
                編輯 {unit.name}
              </button>
              {editingId === unit.id ? (
                <form
                  aria-label={`編輯 ${unit.name}`}
                  onSubmit={(event) => {
                    event.preventDefault();
                    const knowledgeComponentIds = kcText
                      .split(',')
                      .map((value) => value.trim())
                      .filter(Boolean);
                    onUpdate(unit, {
                      name,
                      knowledgeComponentIds,
                    });
                    setEditingId(null);
                  }}
                >
                  <label>
                    單元名稱
                    <input value={name} onChange={(event) => setName(event.target.value)} />
                  </label>
                  <label>
                    知識點 ID（以逗號分隔）
                    <input value={kcText} onChange={(event) => setKcText(event.target.value)} />
                  </label>
                  <button disabled={mutationPending} type="submit">
                    儲存單元
                  </button>
                  <button type="button" onClick={() => setEditingId(null)}>
                    取消
                  </button>
                </form>
              ) : null}
              <button
                disabled={mutationPending}
                type="button"
                onClick={() => onUpdate(unit, { archived: !unit.archived })}
              >
                {unit.archived ? '重新開啟' : '封存'}
              </button>
            </>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

function Management({
  onCreate,
  mutationPending,
  mutationError,
}: Pick<SurfaceProps, 'onCreate' | 'mutationPending' | 'mutationError'>) {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [kcText, setKcText] = useState('');
  if (!onCreate) return null;
  return (
    <div>
      <button
        className="curriculum-action"
        type="button"
        onClick={() => setCreating((value) => !value)}
      >
        新增單元
      </button>
      {creating ? (
        <form
          aria-label="建立課綱單元"
          onSubmit={(event) => {
            event.preventDefault();
            onCreate(
              name,
              kcText
                .split(',')
                .map((value) => value.trim())
                .filter(Boolean),
            );
            setName('');
            setKcText('');
            setCreating(false);
          }}
        >
          <label>
            單元名稱
            <input required value={name} onChange={(event) => setName(event.target.value)} />
          </label>
          <label>
            知識點 ID（以逗號分隔）
            <input value={kcText} onChange={(event) => setKcText(event.target.value)} />
          </label>
          <button disabled={mutationPending} type="submit">
            建立單元
          </button>
        </form>
      ) : null}
      {mutationError ? <p role="alert">課綱操作失敗，請再試一次。</p> : null}
    </div>
  );
}

function Surface({
  status,
  units,
  onRetry,
  onSelect,
  archivedUnits,
  showingArchived = false,
  onShowArchived,
  onCreate,
  onUpdate,
  mutationPending,
  mutationError,
  heading,
}: SurfaceProps & { readonly heading: string }) {
  const visibleUnits = showingArchived ? archivedUnits : units;
  const visibleStatus = status;
  return (
    <section aria-label="課綱" data-curriculum-surface>
      <header>
        <h2>課綱</h2>
        <p>{heading}</p>
      </header>
      <Management
        mutationError={mutationError}
        mutationPending={mutationPending}
        onCreate={onCreate}
      />
      {onShowArchived ? (
        <button
          className="curriculum-action"
          type="button"
          onClick={() => onShowArchived(!showingArchived)}
        >
          {showingArchived ? '返回使用中單元' : '查看已封存單元'}
        </button>
      ) : null}
      {visibleStatus === 'ready' ? (
        <UnitList
          mutationPending={mutationPending}
          onSelect={onSelect}
          onUpdate={onUpdate}
          units={visibleUnits}
        />
      ) : (
        <State onRetry={onRetry} status={visibleStatus} />
      )}
    </section>
  );
}

export function CurriculumDesktopSurface(props: SurfaceProps) {
  return <Surface {...props} heading="管理這個班級的學習單元與知識點順序" />;
}

export function CurriculumMobileSurface(props: SurfaceProps) {
  return <Surface {...props} heading="班級單元" />;
}
